import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, X, Clock, ExternalLink, FileCheck2, AlertCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clientApi } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLUMNS = [
  { id: "todo", label: "À faire", color: "bg-gray-100", text: "text-gray-700" },
  { id: "in_progress", label: "En cours", color: "bg-blue-50", text: "text-blue-700" },
  { id: "review", label: "En révision", color: "bg-amber-50", text: "text-amber-700" },
  { id: "done", label: "Livré", color: "bg-green-50", text: "text-green-700" },
];

const DELIV_STATUS = {
  pending: { label: "À valider", color: "bg-orange-100 text-orange-700", icon: Clock },
  approved: { label: "Approuvé", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rejected: { label: "Révisions demandées", color: "bg-red-100 text-red-700", icon: AlertCircle },
};

export default function ClientProjectDetail() {
  const { id } = useParams();
  const [proj, setProj] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null); // deliverable id
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await clientApi.get(`/client/projects/${id}`);
      setProj(data);
    } catch { toast.error("Projet introuvable"); }
  };
  useEffect(() => { load(); }, [id]);

  if (!proj) return <div className="text-center py-12 text-[#4B5563]">Chargement...</div>;

  const tasksByStatus = STATUS_COLUMNS.reduce((acc, c) => {
    acc[c.id] = (proj.tasks || []).filter((t) => t.status === c.id);
    return acc;
  }, {});
  const totalTasks = (proj.tasks || []).length;
  const doneTasks = tasksByStatus.done.length;
  const progress = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;

  const approve = async (delivId) => {
    setBusy(true);
    try {
      await clientApi.post(`/client/projects/${id}/deliverables/${delivId}/approve`, { comment: "" });
      toast.success("Livrable approuvé !");
      load();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!comment.trim()) { toast.error("Merci d'expliquer la raison du refus."); return; }
    setBusy(true);
    try {
      await clientApi.post(`/client/projects/${id}/deliverables/${rejectOpen}/reject`, { comment });
      toast.success("Révisions demandées");
      setRejectOpen(null); setComment("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="client-project-detail">
      <div>
        <Link to="/portail/projets" className="text-sm text-[#00B8D9] font-semibold hover:underline flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Mes projets</Link>
        <h1 className="font-heading font-black text-3xl text-[#0B1F3A] mt-3">{proj.name}</h1>
        <p className="text-[#4B5563] mt-1">{proj.description}</p>
      </div>

      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold text-[#0B1F3A]">Avancement global</h3>
          <span className="font-heading font-extrabold text-2xl text-[#00B8D9]">{progress}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00B8D9] to-[#0B1F3A] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
          {STATUS_COLUMNS.map((c) => (
            <div key={c.id} className="text-center">
              <div className={`font-bold ${c.text}`}>{tasksByStatus[c.id].length}</div>
              <div className="text-[#4B5563]">{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Kanban */}
      <div>
        <h2 className="font-heading font-bold text-xl text-[#0B1F3A] mb-3">Tableau de bord du projet</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {STATUS_COLUMNS.map((c) => (
            <div key={c.id} className={`${c.color} rounded-xl p-3 min-h-[120px]`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className={`text-xs tracking-wider uppercase font-bold ${c.text}`}>{c.label}</div>
                <div className={`text-xs px-2 py-0.5 rounded-full bg-white ${c.text} font-bold`}>{tasksByStatus[c.id].length}</div>
              </div>
              <div className="space-y-2">
                {tasksByStatus[c.id].length === 0 && <div className="text-xs text-[#4B5563]/60 italic p-2">—</div>}
                {tasksByStatus[c.id].map((t) => (
                  <div key={t.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100" data-testid={`task-${t.id}`}>
                    <div className="font-semibold text-[#0B1F3A] text-sm">{t.title}</div>
                    {t.description && <div className="text-xs text-[#4B5563] mt-1">{t.description}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deliverables */}
      <div>
        <h2 className="font-heading font-bold text-xl text-[#0B1F3A] mb-3 flex items-center gap-2"><FileCheck2 className="h-5 w-5 text-[#00B8D9]" /> Livrables</h2>
        {(proj.deliverables || []).length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center text-[#4B5563] border border-gray-200">
            Aucun livrable pour l'instant. Nous vous notifierons quand un livrable sera prêt à valider.
          </div>
        ) : (
          <div className="space-y-3">
            {proj.deliverables.map((d) => {
              const s = DELIV_STATUS[d.status] || DELIV_STATUS.pending;
              const Icon = s.icon;
              return (
                <div key={d.id} className="bg-white rounded-xl p-5 border border-gray-200" data-testid={`deliverable-${d.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-heading font-bold text-[#0B1F3A]">{d.title}</div>
                      {d.description && <div className="text-sm text-[#4B5563] mt-1">{d.description}</div>}
                      {d.file_url && (
                        <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#00B8D9] font-semibold mt-2 hover:underline">
                          Ouvrir le livrable <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${s.color}`}>
                      <Icon className="h-3 w-3" /> {s.label}
                    </span>
                  </div>
                  {d.client_comment && (
                    <div className="bg-[#F5F7FA] border-l-2 border-[#00B8D9] rounded-r p-3 mt-3 text-sm">
                      <div className="text-xs tracking-wider uppercase font-bold text-[#00B8D9] mb-1">Votre commentaire</div>
                      {d.client_comment}
                    </div>
                  )}
                  {d.status === "pending" && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                      <Button onClick={() => approve(d.id)} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${d.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approuver
                      </Button>
                      <Button onClick={() => setRejectOpen(d.id)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-${d.id}`}>
                        <X className="h-4 w-4 mr-1" /> Demander révision
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!rejectOpen} onOpenChange={(v) => !v && setRejectOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Demander des révisions</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4B5563]">Précisez ce qui doit être modifié — nous reviendrons avec une nouvelle version.</p>
          <Textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ex: couleurs trop foncées, le bouton CTA doit être plus visible..." data-testid="reject-comment" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejectOpen(null)}>Annuler</Button>
            <Button onClick={reject} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-confirm">Envoyer la demande</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
